import {
  Box,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noPayables: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  descriptionCell: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const RecurringPayablesGrid = ({ recurringPayables, isLoading, showExpenseAnalyst }) => {
  const classes = useStyles();

  const formatCurrency = (amount) => {
    if (amount == null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const columns = [
    {
      accessorKey: 'authId',
      header: 'Auth ID',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.authId || '-'}
        </a>
      ),
    },
    {
      accessorKey: 'eeName',
      header: 'EE Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    // Only shown on the expense manager dashboard; EA dashboard rows have no analyst values
    ...(showExpenseAnalyst
      ? [
        {
          accessorKey: 'expenseAnalystName',
          header: 'Expense Analyst',
          Cell: ({ cell }) => cell.getValue() || '-',
        },
      ]
      : []),
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      muiTableBodyCellProps: { className: classes.descriptionCell },
      Cell: ({ row }) => (
        <Tooltip title={row.original.description || ''} arrow>
          <a
            href={`/recurringPayables/${row.original.recurringPayableId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.clickableLink}
          >
            {truncateText(row.original.description)}
          </a>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'payableAmount',
      header: 'Payable Amount',
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: 'numberOfPayments',
      header: 'Payments',
      Cell: ({ cell }) => cell.getValue() ?? '-',
    },
    {
      accessorKey: 'expiresOn',
      header: 'Expires On',
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noPayables} color="textSecondary">
          Loading active recurring payables...
        </Typography>
      </Paper>
    );
  }

  if (recurringPayables.length === 0) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noPayables} color="textSecondary">
          No active recurring payables
        </Typography>
      </Paper>
    );
  }

  return (
    <Box className={classes.tableContainer}>
      <DashboardDataTable
        columns={columns}
        data={recurringPayables}
        initialSorting={[{ id: 'expiresOn', desc: false }]}
      />
    </Box>
  );
};

RecurringPayablesGrid.propTypes = {
  recurringPayables: PropTypes.arrayOf(PropTypes.shape({
    recurringPayableId: PropTypes.number.isRequired,
    fileId: PropTypes.number.isRequired,
    authId: PropTypes.string.isRequired,
    eeName: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    payableAmount: PropTypes.number.isRequired,
    numberOfPayments: PropTypes.number.isRequired,
    expiresOn: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
  isLoading: PropTypes.bool,
  showExpenseAnalyst: PropTypes.bool,
};

RecurringPayablesGrid.defaultProps = {
  isLoading: false,
  showExpenseAnalyst: false,
};

export default RecurringPayablesGrid;
